import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { ResearchProject, KoboAccount, ProjectKoboContext } from '../types';
import { apiService } from '../services/api';

const EditProjectPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<ResearchProject | null>(null);
  const [koboAccounts, setKoboAccounts] = useState<KoboAccount[]>([]);
  const [koboContext, setKoboContext] = useState<ProjectKoboContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    koboAccountId: ''
  });
  const toast = useRef<Toast>(null);

  useEffect(() => {
    if (projectId) {
      loadProject();
      loadKoboAccounts();
      loadKoboContext();
    }
  }, [projectId]);

  useEffect(() => {
    if (project) {
      setFormData({
        title: project.title,
        description: project.description || '',
        koboAccountId: project.koboAccountId || ''
      });
    }
  }, [project]);

  const loadProject = async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      const response = await apiService.getProject(projectId);
      if (response.success && response.data) {
        setProject(response.data);
      } else {
        throw new Error(response.error || 'Project not found');
      }
    } catch (error) {
      toast.current?.show({ 
        severity: 'error', 
        summary: 'Error', 
        detail: 'Failed to load project' 
      });
      navigate('/app/projects');
    } finally {
      setLoading(false);
    }
  };

  const loadKoboAccounts = async () => {
    try {
      const response = await apiService.getUserProfile();
      if (response.success && response.data) {
        setKoboAccounts(response.data.kobo_accounts || []);
      }
    } catch (error) {
      console.error('Failed to load KoboToolbox accounts:', error);
    }
  };

  const loadKoboContext = async () => {
    if (!projectId) return;
    try {
      const response = await apiService.getProjectKoboContext(projectId);
      if (response.success && response.data) {
        setKoboContext(response.data);
      }
    } catch (error) {
      console.error('Failed to load KoboToolbox context:', error);
    }
  };

  const handleSave = async () => {
    if (!projectId) return;
    
    try {
      setSaving(true);
      
      // Update project with all fields in a single request
      const updateResponse = await apiService.updateProject(projectId, {
        title: formData.title,
        description: formData.description,
        koboAccountId: formData.koboAccountId
      });
      
      if (!updateResponse.success) {
        throw new Error(updateResponse.error || 'Failed to update project');
      }
      
      toast.current?.show({ 
        severity: 'success', 
        summary: 'Success', 
        detail: 'Project updated successfully' 
      });
      
      // Reload project data
      await loadProject();
      await loadKoboContext();
      
    } catch (error: any) {
      toast.current?.show({ 
        severity: 'error', 
        summary: 'Error', 
        detail: error.message || 'Failed to update project' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/app/projects/${projectId}`);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: '#3B82F6' }}></i>
      </div>
    );
  }

  if (!project) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <h2 style={{ color: '#1f2937' }}>Project not found</h2>
          <Button label="Back to Projects" onClick={() => navigate('/app/projects')} />
        </div>
      </Card>
    );
  }

  return (
    <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h1 style={{ fontSize: '1.5rem', margin: 0, color: '#1f2937' }}>Edit Project</h1>
          <Button 
            label="Back to Project" 
            icon="pi pi-arrow-left" 
            text 
            onClick={() => navigate(`/app/projects/${projectId}`)} 
          />
        </div>
        <p style={{ color: '#6b7280', margin: 0 }}>
          Update project details and configuration
        </p>
      </div>

      {/* Project Details Form */}
      <Card title="Project Information" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          <div>
            <label htmlFor="title" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#374151' }}>
              Project Title *
            </label>
            <InputText
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Enter project title"
              style={{ width: '100%' }}
              disabled={saving}
            />
          </div>

          <div>
            <label htmlFor="description" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#374151' }}>
              Description
            </label>
            <InputTextarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Enter project description"
              rows={4}
              style={{ width: '100%' }}
              disabled={saving}
            />
          </div>
        </div>
      </Card>

      {/* KoboToolbox Account Configuration */}
      <Card title="KoboToolbox Account" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          <div>
            <label htmlFor="koboAccount" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#374151' }}>
              KoboToolbox Account *
            </label>
            <Dropdown
              id="koboAccount"
              value={formData.koboAccountId}
              options={koboAccounts.map(account => ({
                label: `${account.account_name} (${account.server_url})`,
                value: account.account_id
              }))}
              onChange={(e) => setFormData({ ...formData, koboAccountId: e.value })}
              placeholder="Select KoboToolbox account"
              style={{ width: '100%' }}
              disabled={saving || koboAccounts.length === 0}
            />
            {koboAccounts.length === 0 && (
              <small style={{ color: '#ef4444', marginTop: '0.25rem', display: 'block' }}>
                No KoboToolbox accounts available. Please add an account in your profile settings.
              </small>
            )}
          </div>

          {/* Current KoboToolbox Context */}
          {koboContext && (
            <div style={{ padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
              <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>
                Current Configuration
              </h4>
              <div style={{ display: 'grid', gap: '0.5rem', fontSize: '0.875rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ color: '#6b7280' }}>Account:</span>
                  <span style={{ fontWeight: 500 }}>{koboContext.account_name}</span>
                  <Tag 
                    value={koboContext.account_active ? 'Active' : 'Inactive'} 
                    severity={koboContext.account_active ? 'success' : 'danger'} 
                    style={{ fontSize: '0.75rem' }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ color: '#6b7280' }}>Server:</span>
                  <span>{koboContext.server_url}</span>
                </div>
                {koboContext.deployment_status !== 'not_deployed' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ color: '#6b7280' }}>Deployment Status:</span>
                    <span style={{ textTransform: 'capitalize' }}>{koboContext.deployment_status.replace('_', ' ')}</span>
                  </div>
                )}
                {koboContext.form_id && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ color: '#6b7280' }}>Form ID:</span>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{koboContext.form_id}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Account Change Warning */}
          {formData.koboAccountId !== project.koboAccountId && (
            <div style={{ padding: '0.75rem', backgroundColor: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '6px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                <i className="pi pi-exclamation-triangle" style={{ color: '#f59e0b' }}></i>
                <span style={{ fontSize: '0.875rem', fontWeight: 500, color: '#92400e' }}>
                  Account Change Warning
                </span>
              </div>
              <p style={{ fontSize: '0.75rem', color: '#92400e', margin: 0 }}>
                Changing the KoboToolbox account will affect future deployments. 
                Any existing forms deployed to the previous account will remain there.
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Project Status Information */}
      <Card title="Project Status" style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <div>
            <span style={{ fontSize: '0.875rem', color: '#6b7280', display: 'block' }}>Current Status</span>
            <Tag 
              value={project.status.replace('_', ' ').toUpperCase()} 
              severity={project.status === 'complete' ? 'success' : 'info'}
              style={{ marginTop: '0.25rem' }}
            />
          </div>
          <div>
            <span style={{ fontSize: '0.875rem', color: '#6b7280', display: 'block' }}>Created</span>
            <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>
              {new Date(project.createdAt).toLocaleDateString()}
            </span>
          </div>
          <div>
            <span style={{ fontSize: '0.875rem', color: '#6b7280', display: 'block' }}>Last Updated</span>
            <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>
              {new Date(project.updatedAt).toLocaleDateString()}
            </span>
          </div>
        </div>
      </Card>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
        <Button 
          label="Cancel" 
          icon="pi pi-times" 
          text 
          onClick={handleCancel}
          disabled={saving}
        />
        <Button 
          label="Save Changes" 
          icon="pi pi-check" 
          onClick={handleSave}
          loading={saving}
          disabled={!formData.title.trim() || !formData.koboAccountId}
        />
      </div>

      <Toast ref={toast} />
    </div>
  );
};

export default EditProjectPage;